import { LicenseDetailWithIds } from '@/interfaces/common/common.interface';
import { CompleteLicense } from '@/interfaces/dashboard/licenses';
import { LicenseType, VehicleType, Zone } from '@/interfaces/new-license-request/new-license-request-interfaces';
import { RequestDocuments } from '@/interfaces/requirement';
import { CommonService } from '@/services/common/common.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { RecordType } from '../form.interfaces';
import { EquipmentRow, NewEquipmentRow } from './new-record.interfaces';

@Component({
  selector: 'app-new-record',
  templateUrl: './new-record.component.html',
  styleUrls: ['./new-record.component.css'],
})
export class NewRecordComponent implements OnInit {
  @Input() showClassForm: boolean = false;
  @Input() selectedOption: RecordType = 'NONE';
  @Input() equipmentsMergedControl = new FormControl(null);

  @Input() selectedVehicleControl = new FormControl(null);
  selectedVehicle: CompleteLicense['equipments'][0] | null = null;

  @Output() selectOption = new EventEmitter();
  @Input()
  set license(licenseParam: CompleteLicense | undefined) {
    this._license = licenseParam;
    if (this._license) {
      this.getVehicleTypesByAsset(this._license.asset.id);
      this.getLicenseTypesByAsset(this._license.asset.id);
      this.getConductionZonesByAsset(this._license.asset.id);
    }
  }
  get license(): CompleteLicense | undefined {
    return this._license;
  }
  _license?: CompleteLicense;

  @Input()
  set licenseDetail(licenseDetail: LicenseDetailWithIds | undefined) {
    this._licenseDetail = licenseDetail;
    if (this._licenseDetail) {
      this.gettingDataEvents.controls.licenseDetailDownloaded.setValue(true);
    }
  }
  get licenseDetail(): LicenseDetailWithIds | undefined {
    return this.licenseDetail;
  }
  _licenseDetail?: LicenseDetailWithIds;

  vehicleTypes: VehicleType[] = [];
  licenseTypes: LicenseType[] = [];
  conductionZones: Zone[] = [];

  equipmentsDefault: RequestDocuments[] = [];
  equipmentsNew: RequestDocuments[] = [];
  equipmentsNewToShow: EquipmentRow[] = [];

  gettingDataEvents = new FormGroup({
    vehicleTypesDownloaded: new FormControl(false),
    licenseDetailDownloaded: new FormControl(false),
  });
  addedEquipmentReverseCounter:number = -1;

  get bannedVehicleTypes(): number[] {
    const equipments: RequestDocuments[] = [...this.equipmentsDefault, ...this.equipmentsNew];
    if (equipments) {
      return equipments.map((equipment) => equipment.equipmentId);
    }
    return [];
  }

  constructor(
    private commonService: CommonService,
  ) { }

  ngOnInit(): void {
    this.selectedVehicleControl.valueChanges.subscribe({
      next: (vehicle) => {
        this.selectedVehicle = vehicle;
      },
    });
    this.gettingDataEvents.valueChanges.subscribe({
      next: ({ vehicleTypesDownloaded, licenseDetailDownloaded }) => {
        if (vehicleTypesDownloaded && licenseDetailDownloaded) {
          if (this._licenseDetail) {
            const registeredVehicles = this._licenseDetail.equipments;
            if (this.equipmentsDefault.length === 0) {
              this.equipmentsDefault = registeredVehicles.map((vehicle) => ({
                assetId: this._licenseDetail?.assetId as number,
                equipmentId: vehicle.equipmentId,
                vehicleTypeId: vehicle.vehicleType,
                licenseType: vehicle.licenseTypes.join(),
                conductionZone: vehicle.drivingAreas.join(),
                brand: vehicle.brand,
                model: vehicle.model,
              }));
            }
          }
        }
      },
    });
    // const detailsAndVehicleTypes$ = combineLatest(this.)

  }

  // #region getLists

  getVehicleTypesByAsset(assetId: number) {
    this.commonService.getVehiclesTypesByAsset(assetId).subscribe((res) => {
      this.vehicleTypes = res;
      this.gettingDataEvents.controls.vehicleTypesDownloaded.setValue(true);
    });
  }

  getLicenseTypesByAsset(assetId: number) {
    this.commonService.getLicenseTypesByAsset(assetId).subscribe({
      next: (res) => {
        this.licenseTypes = res;
      },
    });
  }

  getConductionZonesByAsset(assetId: number) {
    this.commonService.getZonesByAsset(assetId).subscribe({
      next: (res) => {
        this.conductionZones = res;
      },
    });
  }

  // #endregion getLists

  onSelectExtendOption(buttonType: RecordType) {
    if (buttonType === 'EQUIPMENT') {
      this.selectedVehicleControl.reset();

    }
    this.selectOption.emit(buttonType);

  }

  mergeEquipmentData() {
    const joinedEquipments: RequestDocuments[] = JSON.parse(JSON.stringify([...this.equipmentsDefault, ...this.equipmentsNew]));
    const mergedEquipments: RequestDocuments[] = [];

    joinedEquipments.map((equipment) => {
      const equipmentInArray = mergedEquipments.find(({ equipmentId }) => equipment.equipmentId === equipmentId);
      if (equipmentInArray) {
        if (equipment.licenseType.length > equipmentInArray.licenseType.length) {
          equipmentInArray.licenseType = equipment.licenseType;
        }
        if (equipment.conductionZone.length > equipmentInArray.conductionZone.length) {
          equipmentInArray.conductionZone = equipment.conductionZone;
        }
      }
      else {
        mergedEquipments.push(equipment);
      }
    });

    // set values only if the merged array has changes
    const equipmentsToShow: EquipmentRow[] = mergedEquipments.reduce<any[]>((arr, equipmentFromMerge) => {
      const fromDefaultArr = this.equipmentsDefault.find(({ equipmentId }) => equipmentId === equipmentFromMerge.equipmentId);
      if (fromDefaultArr) {
        let hasChanges = false;
        if (equipmentFromMerge.licenseType.length > fromDefaultArr.licenseType.length) {
          hasChanges = true;
        }
        if (equipmentFromMerge.conductionZone.length > fromDefaultArr.conductionZone.length) {
          hasChanges = true;
        }

        if (!hasChanges) {
          return arr;
        }
      }

      const equipmentToShow = [equipmentFromMerge].map((equipment) => {
        const vehicleType = this.vehicleTypes.find(({ id }) => +id === +equipment.vehicleTypeId);
        const licenceTypeNames = equipment.licenseType.split(',').map((selectedId) => this.licenseTypes.find(({ id }) => id === +selectedId));
        const conductionZones = equipment.conductionZone.split(',').map((selectedId) => this.conductionZones.find(({ id }) => id === +selectedId));
        return {
          typeName: vehicleType?.name as string,
          licenceTypeNames: licenceTypeNames.map((license) => license?.name).join(' y '),
          conductionZones: conductionZones.map((zone) => zone?.name).join('/'),
          brand: equipment.brand as string,
          model: equipment.model as string,
        };
      });
      return [...arr, equipmentToShow[0]];
    }, []);

    this.equipmentsNewToShow = equipmentsToShow;

    if (this.equipmentsNewToShow.length > 0) {
      this.equipmentsMergedControl.setValue(mergedEquipments);
    }
    else {
      this.equipmentsMergedControl.setValue([]);
    }
  }

  onRemoveEquipment(index: number) {
    // const mergedEquipments = this.equipmentsMergedControl.value as EquipmentRow[];
    const { equipmentsNew, equipmentsNewToShow } = this;

    // Sorting of equipmentsNew to prevent bugs
    const equipmentsNewSortered = equipmentsNew.map((equipment) => ({
      ...equipment,
      typeName: this.vehicleTypes.find(({ id }) => +id === +equipment.equipmentId)?.name,
    })).reduce<any>((equipments, equipment) => {
      let newIndex = -1;

      equipmentsNewToShow.every((equipmentToShow, equipmentToShowIndex) => {
        if (equipmentToShow.typeName === equipment.typeName) {
          newIndex = equipmentToShowIndex;
          return false;
        }
        return true;
      });
      if (newIndex >= 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { typeName, ...newEquipment } = equipment;
        // eslint-disable-next-line no-param-reassign
        equipments[newIndex] = newEquipment;
      }

      return equipments;
    }, []);

    this.equipmentsNew = equipmentsNewSortered;

    this.equipmentsNew.splice(index, 1);
    this.equipmentsNewToShow.splice(index, 1);

    this.mergeEquipmentData();
  }

  onAddEquipment(row: NewEquipmentRow) {
    const newEquipments = [row];

    if (this._licenseDetail) {
      const equipmentDataToSend: RequestDocuments[] = [];
      newEquipments.map((newEquipment) => ({
        ...newEquipment,
        equipmentConductionZones: newEquipment.conductionZones.filter((zone) => zone.active),
        equipmentLicenseTypes: newEquipment.licenseTypes.filter((licenseType) => licenseType.active),
      })).map<RequestDocuments>((newEquipment) => ({
        equipmentId: this.selectedVehicle ? this.selectedVehicle.equipmentId : ++this.addedEquipmentReverseCounter,
        conductionZone: newEquipment.equipmentConductionZones.filter((zone) => zone.active).map(({ id }) => id).join(),
        licenseType: newEquipment.equipmentLicenseTypes.filter((licenseType) => licenseType.active).map(({ id }) => id).join(),
        assetId: this._licenseDetail?.assetId as number,
        vehicleTypeId: +newEquipment.vehicleType,
        brand: newEquipment.brand,
        model: newEquipment.model,
      })).map((equipmentToSend) => {
        equipmentDataToSend.push(equipmentToSend);
      });
      const equipmentInArray = this.equipmentsNew.find((equipment) => equipment.equipmentId === equipmentDataToSend[0].equipmentId);

      if (equipmentInArray) {
        const equipment = equipmentDataToSend[0];
        const jsonEquipment = JSON.stringify(equipmentInArray);
        const jsonNewEquipment = JSON.stringify(equipment);
        // if are the same
        if (jsonEquipment === jsonNewEquipment) {
          return;
        }
        equipmentInArray.licenseType = [...new Set([...equipmentInArray.licenseType.split(','), ...equipment.licenseType.split(',')])].join(',');
        equipmentInArray.conductionZone = [...new Set([...equipmentInArray.conductionZone.split(','), ...equipment.conductionZone.split(',')])].join(',');
      }
      else {
        this.equipmentsNew = [...(this.equipmentsNew || []), ...equipmentDataToSend];
      }

      this.mergeEquipmentData();
    }
  }

}
